<script setup>
import { defineProps, defineEmits, ref, watch } from 'vue';
import { fetchLicensesOptions, fetchLicenseById } from '@/services/api';
import { useLayoutComponents } from '@/composables/layout.js';
import { Sortable } from "sortablejs-vue3";

const { MarkdownRenderer, SelectInput, TextInput } = useLayoutComponents();

const customClauses = ref([]);
const allowedAdditions = ref(false);
const yearsDuration = ref(1);
const licenseId = ref(null);
const licensesOptions = ref([]);
const licenseContent = ref('');
const baseLicenseContent = ref('');
const showCompiled = ref(false);
const expandedClause = ref(null);
const tags = ref([]);
const shortDescription = ref('');
const licensesLoaded = ref(false);
const lastLoadKey = ref('');

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    },
    licenseData: {
        type: Object,
        default: () => {}
    },
    isEvalDataset: {
        type: Boolean,
        default: false
    },
    isOpenSource: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['save', 'close']);

const handleSave = () => {
    const values = {
        licenseId: licenseId.value,
        licenseName: licensesOptions.value.find(license => license.value === licenseId.value).label,
        customClauses: customClauses.value,
        yearsDuration: yearsDuration.value,
    };
    emit('save', values);
}

const handleClose = () => {
    emit('close');
}

const addNewClause = () => {
    customClauses.value.push({
        id: customClauses.value.length + 1,
        name: `New Clause ${customClauses.value.length + 1}`,
        content: '' 
    });
}

const toggleClause = (clauseId) => {
    expandedClause.value = expandedClause.value === clauseId ? null : clauseId;
}

const removeClause = (clauseId) => {
    customClauses.value = customClauses.value.filter(clause => clause.id !== clauseId);
}

watch(licenseId, async (newVal) => {
    if(newVal) {
        const response = await fetchLicenseById(newVal);
        if(response) {
            baseLicenseContent.value = response.content;
            allowedAdditions.value = response.allow_additions;
            customClauses.value = [];
            tags.value = response.tags ?? [];
            shortDescription.value = response.short_description ?? '';
            updateLicenseContent();
        }
    }
});

watch(customClauses, () => {
    updateLicenseContent();
}, { deep: true });

watch(yearsDuration, () => {
    updateLicenseContent();
});

watch(() => props.licenseData, (newData) => {
    if (newData && Object.keys(newData).length > 0) {
        setLicenseData(newData);
    }
}, { deep: true, immediate: true });

const loadLicenses = async () => {
    let tag = null;

    if (props.isEvalDataset) {
        tag = props.isOpenSource ? 'Open Source' : 'Evals';
    }

    const response = await fetchLicensesOptions(tag);
    if (response) {
        const list = Array.isArray(response) ? response : (response.licenses ?? response.data ?? []);
        licensesOptions.value = list.map(license => ({
            label: license.name,
            value: license.id
        }));
    }
};

watch(() => props.show, async (isOpen) => {
    if (isOpen) {
        const currentKey = `${props.isEvalDataset}-${props.isOpenSource}`;
        
        if (!licensesLoaded.value || lastLoadKey.value !== currentKey) {
            await loadLicenses();
            licensesLoaded.value = true;
            lastLoadKey.value = currentKey;
        }
    
        if (props.licenseData && Object.keys(props.licenseData).length > 0) {
            setLicenseData(props.licenseData);
        }
    }
});

const onEnd = (event) => {
    if (event.newIndex !== event.oldIndex) {
        const movedItem = customClauses.value[event.oldIndex];
        const newArray = [...customClauses.value];
        newArray.splice(event.oldIndex, 1);
        newArray.splice(event.newIndex, 0, movedItem);
        customClauses.value = newArray;
    }
};

const updateLicenseContent = () => {
    if (!baseLicenseContent.value) return;
    
    const additionalClauses = convertCustomClausesToMarkdown();
    
    let content = baseLicenseContent.value;
    content = content.replace('{{duration}}', `${yearsDuration.value} year${yearsDuration.value > 1 ? 's' : ''}`);
    content = content.replace('{{additional_clauses}}', additionalClauses);
    
    licenseContent.value = content;
}

const convertCustomClausesToMarkdown = () => {
    let result = '';
    customClauses.value.forEach(clause => {
        result += `## ${clause.name}\n\n${clause.content}\n\n`;
    });
    return result;
}

const setLicenseData = async (data) => {
    if (!data || !data.licenseId) return;
    
    if (data.yearsDuration) {
        yearsDuration.value = data.yearsDuration;
    }
    
    if (data.customClauses && Array.isArray(data.customClauses)) {
        customClauses.value = [...data.customClauses];
    }
    
    if (data.licenseId && licenseId.value !== data.licenseId) {
        licenseId.value = data.licenseId;
    } else if (data.licenseId === licenseId.value) {
        updateLicenseContent();
    }
    if (data.allowedAdditions) {
        allowedAdditions.value = data.allowedAdditions;
    }
}

</script>

<template>
    <div>
        <div v-if="props.show" class="modal-overlay" data-modal>
            <div class="modal">
                <div class="flex flex-col gap-2 py-4">
                    <div class="text-xl font-semibold text-white">
                        Configure License
                    </div>
                    <div class="text-sm text-white/60">
                        Choose a PSDLA variant, set a term and (optionally) append custom clauses if allowed. Preview updates live.
                    </div>

                </div>
                
                <div>
                    <div class="flex flex-col gap-4">
                        <div class="grid grid-cols-2 gap-2 modal-content-grid">
                            <div class="col-span-1 flex flex-col gap-2 left-column">
                                <div class="text-sm font-semibold mb-1"><i class="uil uil-file-alt"></i> 1) Base License</div>
                                <div>
                                    <SelectInput 
                                        id="license-select"
                                        v-model="licenseId" 
                                        :options="licensesOptions" 
                                        label="License"
                                        bgClass="bg-[#3B3B3B]"
                                        borderClass="border-white/20"
                                        hoverBgClass="bg-white/10"
                                        hoverBorderClass="border-white/20"
                                        hoverTextClass="text-white"
                                        focusBorderClass="border-white/20"
                                        dropdownBgClass="bg-[#3B3B3B]"
                                    />
                                </div>
                                <div class="border border-gray-500 rounded-xl p-2 flex flex-col gap-1" v-if="tags.length > 0 || shortDescription">
                                    <div class="flex gap-2" v-if="tags.length > 0">
                                        <div class="text-[10px] text-echoes-background-gray bg-white/70 rounded-full px-2 font-semibold leading-none py-1" v-for="tag in tags" :key="tag">
                                            {{ tag }}
                                        </div>
                                    </div>
                                    <div class="text-xs text-white/50 font-semibold" v-if="shortDescription">
                                        {{ shortDescription }}
                                    </div>
                                </div>
                                <div>
                                    <div class="text-xs font-semibold text-white/60">Duration (years) <i class="uil uil-info-circle"></i></div>
                                    <input 
                                        id="minmax-range" 
                                        type="range" 
                                        min="1" 
                                        max="10" 
                                        v-model="yearsDuration" 
                                        class="custom-range"
                                        :style="{
                                            '--range-progress': `${((yearsDuration - 1) / (10 - 1)) * 100}%`
                                        }"
                                    />
                                    <div class="text-xs font-semibold text-white/60">{{ yearsDuration }} year{{ yearsDuration > 1 ? 's' : '' }}</div>
                                </div>
                                
                                <div v-if="allowedAdditions" class="flex flex-col flex-1 min-h-0 mt-2">                                    
                                    <div class="text-sm font-semibold mb-1"><i class="uil uil-file-alt"></i> 2) Custom Clauses (optional)</div>
                                    <button @click="addNewClause" class="text-sm text-white/80 hover:underline hover:text-white bg-echoes-primary rounded-full px-2 py-1 flex items-center gap-2 w-fit">
                                        <i class="uil uil-plus"></i>
                                        <div>
                                            Add Clause
                                        </div>
                                    </button>
                                    <div v-if="customClauses.length <= 0">
                                        <div class="text-sm text-white/80 mt-2 ">
                                            No custom clauses. Add one to append terms.
                                        </div>
                                    </div>
                                    <div class="clauses-list-container pr-2 mt-2 flex-1">
                                        <Sortable
                                            :list="customClauses"
                                            item-key="id"
                                            tag="div"
                                            @end="onEnd"
                                            :options="{
                                                animation: 150,
                                                group: {
                                                    name: 'customClauses',
                                                    pull: 'clone',
                                                    put: false
                                                }
                                            }"
                                            >
                                        <template #item="{ element }">
                                            <div class="border border-white/20 rounded-xl mb-2">
                                                <div class="p-3 cursor-pointer hover:bg-gray-800/50 transition-colors" @click="toggleClause(element.id)">
                                                    <div class="flex items-center justify-between">
                                                        <div class="flex items-center gap-2">
                                                            <i class="uil uil-align-center-alt cursor-grab active:cursor-grabbing"></i>
                                                            <span class="text-sm font-medium text-white">{{ element.name }}</span>
                                                        </div>
                                                        <i 
                                                            class="uil transition-transform duration-200"
                                                            :class="expandedClause === element.id ? 'uil-angle-up' : 'uil-angle-down'"
                                                        ></i>
                                                    </div>
                                                </div>
                                                <div 
                                                    v-if="expandedClause === element.id"
                                                    class="px-3 pb-3 "
                                                >
                                                    <div class="pt-3 text-sm text-white/80 flex flex-col gap-2">
                                                        <div class="flex gap-2">
                                                            <TextInput 
                                                                :id="`clause-name-${element.id}`"
                                                                v-model="element.name"
                                                                placeholder="Clause name" 
                                                                class="w-2/3" 
                                                                bgClass="bg-[#3B3B3B]"
                                                                borderClass="border-white/20"
                                                                hoverBgClass="bg-white/10"
                                                                hoverBorderClass="border-white/20"
                                                                hoverTextClass="text-white"
                                                            />
                                                            <button @click="removeClause(element.id)"><i class="uil uil-trash-alt text-lg text-red-500 hover:text-red-600 transition-colors"></i></button>
                                                        </div>
                                                        <MarkdownRenderer v-model="element.content" placeholder="Write your content here..." :enableImageUpload="false" />
                                                    </div>
                                                </div>
                                            </div>
                                        </template>
                                    </Sortable>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div class="flex justify-between">
                                    <div>Preview</div>
                                    <label class="inline-flex items-center mb-4 cursor-pointer gap-2">
                                        <span class="text-xs font-semibold text-white/60">Show compiled</span>
                                        <input type="checkbox" v-model="showCompiled" class="sr-only peer">
                                        <div class="relative w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-echoes-primary dark:peer-checked:bg-echoes-primary"></div>
                                    </label>
                                </div>
                                <div class="col-span-1 bg-black rounded-lg license-preview-container p-3" v-if="showCompiled">
                                    <MarkdownRenderer v-model="licenseContent" :contentOnly="true" :enableImageUpload="false" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="absolute bottom-0 left-0 w-full px-4 py-2 flex justify-end items-center gap-2">
                    <div class="text-xs text-white/60">
                        Your changes affect only this listing until saved.
                    </div>
                    <button class="text-white px-4 py-2 rounded-full hover:text-white/80" @click="handleClose">Cancel</button>
                    <button 
                        class="bg-echoes-primary text-white px-4 py-2 rounded-full hover:bg-echoes-primary/80" 
                        :class="{ 'opacity-50 cursor-not-allowed': isLoading || !licenseId }"
                        :disabled="isLoading || !licenseId" 
                        @click="handleSave">Save & Apply</button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="postcss">
.modal-overlay {
    @apply fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-2;
    backdrop-filter: blur(4px);
    animation: fadeIn 0.2s ease;
    isolation: isolate;
}

.modal {
    @apply bg-echoes-background-gray border border-echoes-border-gray/50 rounded-xl shadow-xl flex flex-col h-[95%] px-4 w-2/3;
    animation: slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    z-index: 1;
}

.modal-content-grid {
    height: 75vh;
}

.left-column {
    height: 95%;
    overflow: hidden;
}

.license-preview-container {
    max-height: 60vh;
    height: 60vh;
    overflow-y: scroll;
    overflow-x: hidden;
}

.clauses-list-container {
    overflow-y: scroll;
    overflow-x: hidden;
    min-height: 0;
}

/* Custom Range Slider Styles */
.custom-range {
    @apply w-full h-2 rounded-lg appearance-none cursor-pointer;
    background: linear-gradient(
        to right,
        theme('colors.echoes.primary') 0%,
        theme('colors.echoes.primary') var(--range-progress),
        #374151 var(--range-progress),
        #374151 100%
    );
}

/* Chrome, Safari, Edge */
.custom-range::-webkit-slider-thumb {
    @apply appearance-none w-4 h-4 rounded-full cursor-pointer;
    background: theme('colors.echoes.primary');
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.custom-range::-webkit-slider-thumb:hover {
    @apply scale-110;
    transition: transform 0.2s ease;
}

.custom-range::-webkit-slider-thumb:active {
    @apply scale-95;
}

/* Firefox */
.custom-range::-moz-range-thumb {
    @apply w-4 h-4 rounded-full cursor-pointer border-0;
    background: theme('colors.echoes.primary');
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.custom-range::-moz-range-thumb:hover {
    @apply scale-110;
    transition: transform 0.2s ease;
}

.custom-range::-moz-range-thumb:active {
    @apply scale-95;
}

.custom-range::-moz-range-track {
    @apply bg-transparent;
}

.custom-range::-moz-range-progress {
    background: theme('colors.echoes.primary');
    @apply h-2 rounded-lg;
}

</style>